import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { SelectChangeEvent } from '@mui/material';

import { ATSConnectHiringRequestRequestReferenceTestIds } from 'data-testids/ATS';
import { EmailDynamicSubstitutionObject } from '../types';
import { EmailEditor } from './EmailEditor';
import { processText } from 'features/Connect/features/ConnectATSIntegration/HiringRequests/components/CandidateFeedback/components/Panes/Create/Reference/LandingPage/Request/utils';
import { ReferenceSelectAndLabel } from '../components/ContactTemplateSelect';
import { RequestReferenceCheckProps } from './types';
import { Stack, styled, Typography } from '@mui/material';
import { useQueryGetEmailTemplatesForReferences } from '../queries';
import { useQueryGetHiringRequestListData } from 'features/Connect/features/ConnectATSIntegration/HiringRequests/components/Modal/queries';
import auth from 'utils/auth';

export const RequestReferenceCheck: React.FC<RequestReferenceCheckProps> = ({
  candidateId,
  hiringRequestId,
}) => {
  const [emailTemplateId, setEmailTemplateId] = useState('');

  const { setValue } = useFormContext();
  const recipients = useWatch({ name: 'recipients' });
  const districtId = auth.getUser()?.district?.id;
  const {
    data: emailTemplates,
    error,
    isLoading,
  } = useQueryGetEmailTemplatesForReferences(candidateId);
  const { data } = useQueryGetHiringRequestListData(districtId);
  const currentHiringRequest = data?.results.find((result) => result.id === hiringRequestId);

  const handleChange = (event: SelectChangeEvent<string>) => {
    setEmailTemplateId(event.target.value);

    const selectedEmailTemplate = emailTemplates?.find(
      (emailTemplate) => emailTemplate.id === Number(event.target.value)
    );

    const objectToPass: EmailDynamicSubstitutionObject = {
      candidate: currentHiringRequest.candidate,
      role: currentHiringRequest.role,
      sender: auth.getUser(),
      reference: recipients,
    };

    if (selectedEmailTemplate) {
      const message = processText(selectedEmailTemplate.content, objectToPass);
      const subject = processText(selectedEmailTemplate.subject, objectToPass);
      setValue('message', message, { shouldValidate: true });
      setValue('subject', subject, { shouldValidate: true });
      setValue('reference_id', recipients?.id);
    }
  };

  return (
    <Stack sx={{ pt: 1 }} spacing={2}>
      <SelectText variant="heading7">Select email template</SelectText>
      <ReferenceSelectAndLabel
        items={emailTemplates}
        dataTestId={ATSConnectHiringRequestRequestReferenceTestIds.EMAIL_TEMPLATE_SELECT}
        labelText=""
        value={emailTemplateId}
        placeholderText="Select Email Template"
        hasError={Boolean(error)}
        onChange={handleChange}
        isLoading={isLoading}
        shouldBeDisabled={!recipients}
      />
      <EmailEditor
        dataTestId={ATSConnectHiringRequestRequestReferenceTestIds.EMAIL_TEMPLATE_SELECT}
      />
    </Stack>
  );
};

export const SelectText = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));
